import { IN_APP } from 'constants/messageMediums';
import { selectEmail, selectIsWebInAppReportingFeatureFlagOn } from 'core/userContext/selectors';
import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { BUTTON_SIZES, BUTTON_VARIANTS, GhostButton, IconButton } from 'ui/button';
import { Delete16, Edit16, Export16, Favorite16, Link16, Overflow24, Save16 } from 'ui/icons';
import { Menu, MenuItemOption } from 'ui/menu';
import { MenuListContainerWrapper } from 'ui/menu/Styled';
import { ALIGNMENT } from 'ui/menu/constants';
import { colors, spacing } from 'ui/theme';
import { TipPlacement, Tooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import {
  deleteSavedViewsRequest,
  fetchProjectDefaultViewResponse,
  fetchRecentSavedViewsRequest,
  fetchSavedViewResponse,
  resetPDFReport,
} from '../actions';
import { MIExportPDF, RECENTLY_VIEWED_LOCAL_STORAGE_KEY } from '../constants';
import { useCampaignAndMetricFiltersContext, useViewContext } from '../context';
import { PDFReportFormProvider } from '../context/PDFReportFormProvider';
import { useRestQueryParams } from '../hooks/useRestQueryParams';
import {
  selectCampaignIds,
  selectPDFReportUrl,
  selectProjectDefaultView,
  selectSavedView,
} from '../selectors';
import { RestDataIdentifier } from '../types';
import { getViewIdsQueryString } from '../utils';
import { ExportEventModal } from './ExportEventModal';
import { emptyStateTitles } from './MultiMediumEmptyState';
import { PDFReportModal } from './PDFReport';
import { SaveViewButton } from './SavedViews/SaveViewButton';
import { SavedViewsDropdown } from './SavedViews/SavedViewsDropdown';
import { useGetUnsavedSettings } from './SavedViews/hooks/useGetUnsavedSettings';
import { useViewsModals } from './SavedViews/hooks/useViewsModals';
import { DeleteViewModal } from './SavedViews/modals/DeleteViewModal';
import { SetDefaultModal } from './SavedViews/modals/SetDefaultModal';
import { ShareViewModal } from './SavedViews/modals/ShareViewModal';
import { ShareUrlPopUp } from './ShareUrlPopUp';
import { selectEnabledPreviews } from 'modules/app/selectors';

type Props = {
  totalCampaigns: number;
  totalSelectedCrossMetrics: number;
  totalSelectedMediums: number;
};

const PageActionContainer = styled.div`
  display: flex;
  z-index: 4;
  gap: ${spacing.half};
`;

const Label = styled.div`
  display: flex;
  align-items: center;
`;

const ResetButton = styled(GhostButton)`
  &&& {
    &[aria-disabled='true'] {
      background: none;
      border-color: transparent;
      box-shadow: none;
    }
  }
`;

const IconWrapper = styled.div`
  margin-right: ${spacing.half};
  width: ${pxToRem(18)};
  height: ${pxToRem(18)};
`;

const StyledMenu = styled(Menu)`
  &[data-large-menu='true'] {
    ${MenuListContainerWrapper} {
      min-width: ${spacing.x12};
    }
  }

  [data-test='tooltip-trigger'] {
    cursor: default;
    color: ${colors.textDisabled};
  }
`;

const exportPDFTooltipMessages = {
  exceedsMetricLimit:
    'PDF reports can only support up to 10 metrics at a time. Please select 10 or less, then try again.',
  noMediums: emptyStateTitles.noMediums.title,
  noSelectedMetrics: emptyStateTitles.noSelectedMetrics.title,
  noCampaigns: emptyStateTitles.noCampaigns.title,
};

type ExportPDFConditionKey = keyof typeof exportPDFTooltipMessages;

export const PageActions: FC<React.PropsWithChildren<Props>> = ({
  totalCampaigns,
  totalSelectedCrossMetrics = 0,
  totalSelectedMediums = 0,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const enabledPreviews = useSelector(selectEnabledPreviews);
  const inAppFF = useSelector(selectIsWebInAppReportingFeatureFlagOn);
  const viewModals = useViewsModals();
  const view = useSelector(selectSavedView);
  const userEmail = useSelector(selectEmail);
  const projectDefaultView = useSelector(selectProjectDefaultView);
  const campaignIds = useSelector(selectCampaignIds);
  const PDFReportUrl = useSelector(selectPDFReportUrl);
  const { toggleRefetchDropdownViews, toggleResetView } = useViewContext();
  const unsavedSettings = useGetUnsavedSettings();
  const { selectedMediums } = useCampaignAndMetricFiltersContext();
  const [, setRecentlyViewedIds, getRecentlyViewedIds] = useLocalStorage<string[]>(
    RECENTLY_VIEWED_LOCAL_STORAGE_KEY,
    [],
  );

  const [shareUrlOpen, setShareUrlOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportInAppEventModalOpen, setIsExportInAppEventModalOpen] = useState(false);
  const [isPDFReportModalOpen, setIsPDFReportModalOpen] = useState(false);
  const {
    metrics: { overallMetrics = [], mediumMetrics = [] } = {},
  } = useRestQueryParams(RestDataIdentifier.PDF_REPORT);
  const shouldShowExportPDFOption =
    useFeatureFlag(MIExportPDF) || enabledPreviews.includes('PDFReport');
  const shouldShowDeleteOption = view?.createdBy === userEmail;
  const shouldShowSetDefaultOption = view?.isPrivate === false && view?.isProjectDefault === false;
  const exceedsMetricLimit = [...overallMetrics, ...mediumMetrics].length > 10;

  const exportPDFTooltipConditions: Record<ExportPDFConditionKey, boolean> = {
    exceedsMetricLimit,
    noMediums: totalSelectedMediums === 0,
    noSelectedMetrics: totalSelectedCrossMetrics === 0,
    noCampaigns: totalCampaigns === 0,
  };
  // Find the first condition that is true
  const triggeringCondition = Object.keys(exportPDFTooltipConditions).find(
    (key) => exportPDFTooltipConditions[key as ExportPDFConditionKey],
  ) as ExportPDFConditionKey | undefined;

  const exportPDFTooltipContent = triggeringCondition
    ? exportPDFTooltipMessages[triggeringCondition]
    : '';

  // Derive isExportPDFOptionDisabled from whether tooltip content exists
  const isExportPDFOptionDisabled = Boolean(exportPDFTooltipContent);

  const IDS = {
    exportEvent: 'export-event',
    exportInAppBreakdown: 'export-in-app-breakdown',
    exportPDF: 'export-pdf-report',
    editViewProps: 'edit-view-props',
    saveAsNew: 'save-as-new',
    share: 'share',
    delete: 'delete',
    setDefault: 'setDefault',
  };

  const overflowActions = {
    [IDS.exportEvent]: () => {
      setIsExportModalOpen(true);
    },
    [IDS.exportInAppBreakdown]: () => setIsExportInAppEventModalOpen(true),
    [IDS.exportPDF]: () => setIsPDFReportModalOpen(true),
    [IDS.editViewProps]: () => {
      viewModals.edit.toggle(true);
    },
    [IDS.saveAsNew]: () => {
      viewModals.create.toggle(true);
    },
    [IDS.share]: () => {
      viewModals.share.toggle(true);
    },
    [IDS.delete]: () => {
      viewModals.delete.toggle(true);
    },
    [IDS.setDefault]: () => {
      viewModals.setDefault.toggle(true);
    },
  };

  const viewOptions: MenuItemOption[] = [
    ...(view
      ? [
          {
            content: (
              <Label>
                <IconWrapper>
                  <Edit16 color={colors.textSecondary} />
                </IconWrapper>
                Edit saved view
              </Label>
            ),
            id: IDS.editViewProps,
          },
          {
            content: (
              <Label>
                <IconWrapper>
                  <Save16 color={colors.textSecondary} />
                </IconWrapper>
                Save as new
              </Label>
            ),
            id: IDS.saveAsNew,
          },
          ...(shouldShowSetDefaultOption
            ? [
                {
                  content: (
                    <Label>
                      <IconWrapper>
                        <Favorite16 color={colors.textSecondary} />
                      </IconWrapper>
                      Set as default
                    </Label>
                  ),
                  id: IDS.setDefault,
                },
              ]
            : []),
          ...(shouldShowDeleteOption
            ? [
                {
                  content: (
                    <Label>
                      <IconWrapper>
                        <Delete16 color={colors.textSecondary} />
                      </IconWrapper>
                      Delete
                    </Label>
                  ),
                  id: IDS.delete,
                },
              ]
            : []),
        ]
      : []),
    {
      content: (
        <Label>
          <IconWrapper>
            <Link16 color={colors.textSecondary} />
          </IconWrapper>
          Share
        </Label>
      ),
      id: IDS.share,
    },
  ];

  const menuOptions: MenuItemOption[] = [
    ...viewOptions,
    {
      content: (
        <Label>
          <IconWrapper>
            <Export16 color={colors.textSecondary} />
          </IconWrapper>
          Export events
        </Label>
      ),
      id: IDS.exportEvent,
    },
  ];
  if (inAppFF && selectedMediums.includes(IN_APP)) {
    menuOptions.push({
      content: (
        <Label>
          <IconWrapper>
            <Export16 color={colors.textSecondary} />
          </IconWrapper>
          Export in-app events by destination
        </Label>
      ),
      id: IDS.exportInAppBreakdown,
    });
  }
  if (shouldShowExportPDFOption) {
    menuOptions.push({
      content: (
        <Tooltip
          isEnabled={isExportPDFOptionDisabled}
          tooltipContent={exportPDFTooltipContent}
          placement={TipPlacement.Left}
          automaticallyAvoidCollisions
        >
          <Label>
            <IconWrapper>
              <Export16 color={colors.textSecondary} />
            </IconWrapper>
            Export PDF report
          </Label>
        </Tooltip>
      ),
      id: IDS.exportPDF,
      isDisabled: isExportPDFOptionDisabled,
    });
  }

  /**
   * Callback to be executed after a delete happens. It will;
   * 1) remove the id of the deleted view from the recentlyVisitedIds stored in the local storage
   * 2) fetch the recentlyVisitedIds from the BE with the updated id list (for the saved views dropdown)
   * 3) check if there is a project default and the deleted view is the project default
   * 4) if it is not the project default, it will populate the page with the project default
   * 5) if it is the project default, it will clear out the savedView and the project default from the reducer state
   * 6) in any case it will fetch the options for dropdown
   */
  const deleteViewCallback = () => {
    const recentlyVisitedIds = getRecentlyViewedIds();
    const updatedRecentlyViewedIds = recentlyVisitedIds.filter((id) => id !== `${view.id}`);
    setRecentlyViewedIds(updatedRecentlyViewedIds);

    if (updatedRecentlyViewedIds?.length)
      dispatch(fetchRecentSavedViewsRequest(getViewIdsQueryString(updatedRecentlyViewedIds)));
    if (projectDefaultView && view.id !== projectDefaultView.id) {
      const newPath = `/messagingInsights/views/${projectDefaultView.id}`;
      history.replace(newPath);
    } else {
      dispatch(fetchSavedViewResponse());
      history.push('/messagingInsights');
      dispatch(fetchProjectDefaultViewResponse());
    }
    toggleRefetchDropdownViews();
  };

  const deleteView = () => {
    dispatch(deleteSavedViewsRequest(view, deleteViewCallback));
  };

  return (
    <PageActionContainer>
      <>
        <>
          {unsavedSettings && (
            <ResetButton
              size={BUTTON_SIZES.LARGE}
              onClick={() => {
                toggleResetView();
              }}
            >
              Reset
            </ResetButton>
          )}
          <SavedViewsDropdown />
          <SaveViewButton />
          {viewModals.share.isShown && (
            <ShareViewModal view={view} onClose={() => viewModals.share.toggle(false)} />
          )}
          {viewModals.setDefault.isShown && <SetDefaultModal />}
          {viewModals.delete.isShown && (
            <DeleteViewModal
              viewsToBeDeleted={[view]}
              deleteViews={deleteView}
              onClose={() => viewModals.delete.toggle(false)}
            />
          )}
        </>
      </>
      {shareUrlOpen && <ShareUrlPopUp setIsOpen={setShareUrlOpen} />}
      <StyledMenu
        data-test="messaging-insights-menu"
        data-large-menu={shouldShowExportPDFOption}
        id="messaging-insights-menu"
        handleSelectionChange={(id: string) => id && overflowActions[id]()}
        horizontalAlignment={ALIGNMENT.RIGHT}
        options={menuOptions}
      >
        <IconButton variant={BUTTON_VARIANTS.SECONDARY} size={BUTTON_SIZES.LARGE}>
          <Overflow24 />
        </IconButton>
      </StyledMenu>
      {isExportModalOpen && (
        <ExportEventModal
          isExportModalOpen={isExportModalOpen}
          setIsExportModalOpen={setIsExportModalOpen}
          selectedMediums={selectedMediums}
          selectedIds={campaignIds}
          showAppPackage={false}
        />
      )}
      {isExportInAppEventModalOpen && (
        <ExportEventModal
          isExportModalOpen={isExportInAppEventModalOpen}
          setIsExportModalOpen={setIsExportInAppEventModalOpen}
          selectedMediums={['InApp']}
          selectedIds={campaignIds}
          showAppPackage
        />
      )}
      {isPDFReportModalOpen && (
        <PDFReportFormProvider>
          <PDFReportModal
            isPDFReportModalOpen={isPDFReportModalOpen}
            onClose={() => {
              setIsPDFReportModalOpen(false);
              dispatch(resetPDFReport(PDFReportUrl));
            }}
          />
        </PDFReportFormProvider>
      )}
    </PageActionContainer>
  );
};

export default PageActions;
